import React, { useState, useCallback, useMemo, memo } from 'react';
import { Badge, Checkbox } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import { Person } from '#root/queries/persons';
import MatchCard from '../MatchCard';
import './styles.scss';


export interface MatchGroupProps {
    victim: Person;
    matchedPublicPersons: Person[];
    selectedIds: Set<string>;
    onSelectPublicPerson: (publicPerson: Person) => void;
    unit: string;
}

const MatchGroup: React.FC<MatchGroupProps> = memo(({
    victim,
    matchedPublicPersons,
    selectedIds,
    onSelectPublicPerson,
    unit,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const toggleCollapsed = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);

    const isGroupSelected = matchedPublicPersons.length > 0 &&
        matchedPublicPersons.every(p => !!p.id && selectedIds.has(p.id));

    const handleGroupCheckbox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        const checked = e.target.checked;
        matchedPublicPersons.forEach(pub => {
            if (!pub.id) return;
            const alreadySelected = selectedIds.has(pub.id);
            if (checked && !alreadySelected) onSelectPublicPerson(pub);
            if (!checked && alreadySelected) onSelectPublicPerson(pub);
        });
    }, [matchedPublicPersons, selectedIds, onSelectPublicPerson]);

    const totalCount = matchedPublicPersons.length;
    const contactedCount = matchedPublicPersons.filter(p => p.isConnected).length;

    // Sort so the most-frequent name appears first; ties keep original order.
    const sortedPublicPersons = useMemo(() => {
        if (matchedPublicPersons.length <= 1) return matchedPublicPersons;

        const nameCounts = new Map<string, number>();
        for (const p of matchedPublicPersons) {
            const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
            nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
        }

        return [...matchedPublicPersons].sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
            return (nameCounts.get(nameB) || 0) - (nameCounts.get(nameA) || 0);
        });
    }, [matchedPublicPersons]);

    // Single match — render as a plain MatchCard with a "Matched" badge
    if (totalCount === 1) {
        return (
            <div className="match-group">
                <MatchCard
                    victim={victim}
                    publicPerson={matchedPublicPersons[0]}
                    isSelected={matchedPublicPersons[0].id ? selectedIds.has(matchedPublicPersons[0].id) : false}
                    onSelect={onSelectPublicPerson}
                    unit={unit}
                    showMatchedBadge
                />
            </div>
        );
    }

    if (isCollapsed) {
        return (
            <div
                className="match-group match-group--collapsed"
                onClick={toggleCollapsed}
                tabIndex={0}
                role="button"
                aria-expanded={false}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleCollapsed(); }}
            >
                {/* Stack layers for visual depth */}
                <div className="match-group__stack-layer match-group__stack-layer--2" />
                <div className="match-group__stack-layer match-group__stack-layer--1" />

                {/* Top card — preview only; clicks expand the group, not select */}
                <div className="match-group__top-card" onClick={e => e.stopPropagation()}>
                    <MatchCard
                        victim={victim}
                        publicPerson={sortedPublicPersons[0]}
                        isSelected={false}
                        onSelect={toggleCollapsed}
                        unit={unit}
                    />
                </div>

                {/* Badges + expand button in upper-right */}
                <div className="match-group__badges">
                    <Badge appearance="filled" color="brand" size="medium">
                        Matched ({totalCount})
                    </Badge>
                    {contactedCount > 0 && (
                        <Badge appearance="filled" color="success" size="medium">
                            Contacted ({contactedCount})
                        </Badge>
                    )}
                    <button
                        className="match-group__toggle-btn"
                        onClick={(e) => { e.stopPropagation(); setIsCollapsed(false); }}
                        title="Expand group"
                        aria-label="Expand group"
                    >
                        <ChevronDownRegular />
                    </button>
                </div>
            </div>

        );
    }

    // Expanded state
    return (
        <div className="match-group match-group--expanded">
            <div className="match-group__header">
                {/* Left: Select Group checkbox */}
                <div className="match-group__header-left" onClick={e => e.stopPropagation()}>
                    <Checkbox
                        checked={isGroupSelected}
                        onChange={handleGroupCheckbox}
                        label={`Select All (${matchedPublicPersons.length})`}
                    />
                </div>

                {/* Right: collapse button */}
                <div className="match-group__header-right">
                    <button
                        className="match-group__toggle-btn"
                        onClick={toggleCollapsed}
                        title="Collapse group"
                        aria-label="Collapse group"
                        aria-expanded={true}
                    >
                        <ChevronUpRegular />
                    </button>
                </div>
            </div>

            <div className="match-group__list">
                {sortedPublicPersons.map(pub => (
                    <MatchCard
                        key={pub.id!}
                        victim={victim}
                        publicPerson={pub}
                        isSelected={pub.id ? selectedIds.has(pub.id) : false}
                        onSelect={onSelectPublicPerson}
                        unit={unit}
                    />
                ))}
            </div>
        </div>
    );
});

MatchGroup.displayName = 'MatchGroup';

export default MatchGroup;
